import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { listRooms, createRoom, updateRoom, deleteRoom, uploadRoomImages, removeImage } from '../../services/roomService';
import { toast } from 'react-toastify';
import './RoomManagement.css';

const RoomManagement = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hotelId = searchParams.get('hotel');

  const [loader, setLoader] = useState(false);
  const [roomList, setRoomList] = useState([]);
  const [listView, setListView] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage] = useState(10);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteRoomId, setDeleteRoomId] = useState(null);

  const [room, setRoom] = useState({
    name: '',
    image: [],
    price: 0,
    short_desc: '',
    room_price: 0,
    max_person: 0,
    min_person: 0,
    default_person: 0,
    room_type: '',
    total_rooms: 0,
    available_rooms: 0,
    hotel: hotelId,
    cancellation_policy: {
      description: '',
      allowed: true,
      cancellation_time: 0,
      cancellation_charge: 0,
    },
    facilities: [],
    features: [],
    extra_price_person: 0
  });

  useEffect(() => {
    if (!hotelId) {
      toast.error('Hotel ID is required');
      navigate('/dashboard');
      return;
    }
    fetchRoomList();
  }, [hotelId, currentPage]);

  const fetchRoomList = async () => {
    try {
      const response = await listRooms({
        query: { hotel: hotelId },
        skip: (currentPage - 1) * rowsPerPage,
        limit: rowsPerPage,
      });

      setRoomList(response.data?.records || []);
      setTotalRecords(response.data?.totalRecords || 0);
    } catch (error) {
      console.error('Failed to fetch rooms:', error);
      toast.error(error.message || 'Failed to load rooms');
    }
  };

  const resetRoom = () => {
    setRoom({
      name: '',
      image: [],
      price: 0,
      short_desc: '',
      room_price: 0,
      max_person: 0,
      min_person: 0,
      default_person: 0,
      room_type: '',
      total_rooms: 0,
      available_rooms: 0,
      hotel: hotelId,
      cancellation_policy: {
        description: '',
        allowed: true,
        cancellation_time: 0,
        cancellation_charge: 0,
      },
      facilities: [],
      features: [],
      extra_price_person: 0
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      if (child === 'allowed') {
        setRoom({ ...room, [parent]: { ...room[parent], [child]: checked } });
      } else if (child === 'cancellation_charge') {
        if (parseFloat(value) <= room.price || value === '') {
          setRoom({ ...room, [parent]: { ...room[parent], [child]: value } });
        }
      } else {
        setRoom({ ...room, [parent]: { ...room[parent], [child]: value } });
      }
    } else {
      setRoom({ ...room, [name]: value });

      if (name === 'room_price') {
        calculatePrice(room.facilities, value);
      }
    }
  };

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);

    if (files.length > 10) {
      toast.error('Only 10 files allowed at once');
      return;
    }

    if (room.image.length + files.length > 10) {
      toast.error('Maximum 10 images allowed per room');
      return;
    }

    setLoader(true);
    try {
      const response = await uploadRoomImages(files);
      const uploadedUrls = response.data?.map(img => img.location) || [];

      setRoom({ ...room, image: [...room.image, ...uploadedUrls] });
      toast.success('Images uploaded successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to upload images');
    } finally {
      setLoader(false);
    }
  };

  const handleImageRemove = async (imagePath, index) => {
    try {
      await removeImage(imagePath);
      const newImages = [...room.image];
      newImages.splice(index, 1);
      setRoom({ ...room, image: newImages });
      toast.success('Image removed successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to remove image');
    }
  };

  const addFacility = () => {
    setRoom({
      ...room,
      facilities: [...room.facilities, { name: '', desc: '', price: '' }]
    });
  };

  const deleteFacility = (index) => {
    const facilities = [...room.facilities];
    facilities.splice(index, 1);
    calculatePrice(facilities, room.room_price);
  };

  const handleFacilityChange = (index, field, value) => {
    const facilities = [...room.facilities];
    facilities[index][field] = value;

    if (field === 'price') {
      calculatePrice(facilities, room.room_price);
    } else {
      setRoom({ ...room, facilities });
    }
  };

  const addFeature = () => {
    setRoom({
      ...room,
      features: [...room.features, { type: '', desc: '' }]
    });
  };

  const deleteFeature = (index) => {
    const features = [...room.features];
    features.splice(index, 1);
    setRoom({ ...room, features });
  };

  const handleFeatureChange = (index, field, value) => {
    const features = [...room.features];
    features[index][field] = value;
    setRoom({ ...room, features });
  };

  const calculatePrice = (facilities = room.facilities, roomPrice = room.room_price) => {
    const facilitiesPrice = facilities.reduce((acc, facility) => {
      return acc + parseFloat(facility.price || 0);
    }, 0);

    setRoom({
      ...room,
      room_price: roomPrice,
      facilities: facilities,
      price: parseFloat(roomPrice || 0) + parseFloat(facilitiesPrice || 0),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!room.facilities.length) {
      toast.error('Please add at least one facility');
      return;
    }

    if (!room.features.length) {
      toast.error('Please add at least one feature');
      return;
    }

    if (!room.image.length) {
      toast.error('Please upload at least one image');
      return;
    }

    try {
      if (room._id) {
        await updateRoom(room._id, room);
        toast.success('Room updated successfully');
      } else {
        await createRoom(room);
        toast.success('Room created successfully');
      }

      resetRoom();
      fetchRoomList();
      setListView(true);
    } catch (error) {
      toast.error(error.message || 'Failed to save room');
    }
  };

  const handleEdit = (roomData) => {
    // Calculate room_price by removing facilities price
    const facilitiesPrice = roomData.facilities?.reduce((acc, obj) => {
      return acc + parseFloat(obj.price || 0);
    }, 0) || 0;

    const baseRoomPrice = parseFloat(roomData.price) - facilitiesPrice;

    setRoom({
      ...roomData,
      room_price: baseRoomPrice,
    });
    setListView(false);
  };

  const confirmDelete = (roomId) => {
    setDeleteRoomId(roomId);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    try {
      await deleteRoom(deleteRoomId);
      toast.success('Room deleted successfully');
      fetchRoomList();
      setShowDeleteDialog(false);
      setDeleteRoomId(null);
    } catch (error) {
      toast.error(error.message || 'Failed to delete room');
    }
  };

  const totalPages = Math.ceil(totalRecords / rowsPerPage);

  return (
    <div className="room-management-page">
      <div className="container">
        <div className="room-header">
          <h1 className="room-title">Room Management</h1>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/dashboard')}
          >
            Back to Hotels
          </button>
        </div>

        {listView ? (
          // Room List View
          <div className="room-card">
            <div className="card-header">
              <h2>Rooms List</h2>
              <button
                className="btn btn-primary"
                onClick={() => {
                  resetRoom();
                  setListView(false);
                }}
              >
                + Add Room
              </button>
            </div>

            {roomList.length > 0 ? (
              <>
                <div className="table-responsive">
                  <table className="room-table">
                    <thead>
                      <tr>
                        <th>Image</th>
                        <th>Name</th>
                        <th>Room Type</th>
                        <th>Total Rooms</th>
                        <th>Available Rooms</th>
                        <th>Price</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roomList.map((roomItem) => (
                        <tr key={roomItem._id}>
                          <td data-label="Image">
                            {roomItem.image?.[0] && (
                              <img
                                src={roomItem.image[0]}
                                alt={roomItem.name}
                                className="room-thumbnail"
                              />
                            )}
                          </td>
                          <td data-label="Name" className="font-weight-bold">{roomItem.name}</td>
                          <td data-label="Room Type">{roomItem.room_type}</td>
                          <td data-label="Total Rooms">{roomItem.total_rooms}</td>
                          <td data-label="Available Rooms">{roomItem.available_rooms}</td>
                          <td data-label="Price">₹{roomItem.price}</td>
                          <td data-label="Actions">
                            <div className="action-buttons">
                              <button
                                className="btn-icon btn-edit"
                                title="Edit"
                                onClick={() => handleEdit(roomItem)}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                </svg>
                              </button>
                              <button
                                className="btn-icon btn-delete"
                                title="Delete"
                                onClick={() => confirmDelete(roomItem._id)}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <polyline points="3 6 5 6 21 6"></polyline>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <span className="page-info">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="empty-state">
                <p>No rooms added yet</p>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    resetRoom();
                    setListView(false);
                  }}
                >
                  Add Your First Room
                </button>
              </div>
            )}
          </div>
        ) : (
          // Room Form View
          <div className="room-card">
            <div className="card-header">
              <h2>{room._id ? 'Edit Room' : 'Add New Room'}</h2>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  resetRoom();
                  setListView(true);
                }}
              >
                Back to List
              </button>
            </div>

            <form onSubmit={handleSubmit} className="room-form">
              {/* Basic Information */}
              <div className="form-section">
                <h3>Basic Information</h3>
                <div className="row">
                  <div className='col-md-6'>
                    <div className="form-group">
                      <label>Room Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={room.name}
                        onChange={handleChange}
                        required
                        placeholder="e.g., Deluxe Room"
                      />
                    </div>
                  </div>
                  <div className='col-md-6'>
                    <div className="form-group">
                      <label>Room Type *</label>
                      <input
                        type="text"
                        name="room_type"
                        value={room.room_type}
                        onChange={handleChange}
                        required
                        placeholder="e.g., Deluxe, Suite, Standard"
                      />
                    </div>
                  </div>
                  <div className='col-md-12'>
                    <div className="form-group">
                      <label>Short Description *</label>
                      <input
                        type="text"
                        name="short_desc"
                        value={room.short_desc}
                        onChange={handleChange}
                        required
                        placeholder="Brief description of the room"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Occupancy Details */}
              <div className="form-section">
                <h3>Occupancy Details</h3>
                <div className="row">
                  <div className='col-md-4'>
                    <div className="form-group">
                      <label>Default Person *</label>
                      <input
                        type="number"
                        name="default_person"
                        value={room.default_person}
                        onChange={handleChange}
                        required
                        min="1"
                      />
                    </div>
                   </div> 
                  <div className='col-md-4'>
                    <div className="form-group">
                      <label>Min Person *</label>
                      <input
                        type="number"
                        name="min_person"
                        value={room.min_person}
                        onChange={handleChange}
                        required
                        min="1"
                      />
                    </div>
                  </div>
                  <div className='col-md-4'>
                    <div className="form-group">
                      <label>Max Person *</label>
                      <input
                        type="number"
                        name="max_person"
                        value={room.max_person}
                        onChange={handleChange}
                        required
                        min="1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Inventory */}
              <div className="form-section">
                <h3>Inventory</h3>

                {/* Single Row: Total Rooms and Available Rooms */}
                <div className="row" >
                  <div className='col-md-6'>
                    <div className="form-group">
                      <label>Total Rooms *</label>
                      <input
                        type="number"
                        name="total_rooms"
                        value={room.total_rooms}
                        onChange={handleChange}
                        required
                        min="1"
                      />
                    </div>
                  </div>
                  <div className='col-md-6'>
                    <div className="form-group">
                      <label>Available Rooms *</label>
                      <input
                        type="number"
                        name="available_rooms"
                        value={room.available_rooms}
                        onChange={handleChange}
                        required
                        min="0"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div className="form-section">
                <h3>Pricing</h3>

                {/* First Row: Base Room Price and Extra Price Per Person */}
                <div className="row">
                  <div className='col-md-6'>
                    <div className="form-group">
                      <label>Base Room Price *</label>
                      <input
                        type="number"
                        name="room_price"
                        value={room.room_price}
                        onChange={handleChange}
                        required
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                  <div className='col-md-6'>
                    <div className="form-group">
                      <label>Extra Price Per Person *</label>
                      <input
                        type="number"
                        name="extra_price_person"
                        value={room.extra_price_person}
                        onChange={handleChange}
                        required
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>

                {/* Second Row: Total Price (Full Width) */}
                <div className="form-grid" style={{ marginTop: '1.5rem' }}>
                  <div className="form-group">
                    <label>Total Price (Calculated)</label>
                    <div className="price-display">₹{room.price}</div>
                  </div>
                </div>
              </div>

              {/* Cancellation Policy */}
              <div className="form-section">
                <h3>Cancellation Policy</h3>

                {/* Toggle Switch */}
                <div className="toggle-group">
                  <label htmlFor="cancellation-toggle">Cancellation Allowed</label>
                  <div>
                    <label className="toggle-switch">
                      <input
                        id="cancellation-toggle"
                        type="checkbox"
                        name="cancellation_policy.allowed"
                        checked={room.cancellation_policy.allowed}
                        onChange={handleChange}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    <span className={`toggle-state-text ${room.cancellation_policy.allowed ? 'active' : ''}`}>
                      {room.cancellation_policy.allowed ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>

                {room.cancellation_policy.allowed && (
                  <>
                    {/* First Row: Description (Full Width) */}
                    <div className="row">
                      <div className="col-md-12">
                        <div className="form-group">
                          <label>Description *</label>
                          <input
                            type="text"
                            name="cancellation_policy.description"
                            value={room.cancellation_policy.description}
                            onChange={handleChange}
                            required
                            placeholder="Cancellation policy description"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Second Row: Cancellation Time and Charge */}
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Cancellation Time (Days) *</label>
                          <input
                            type="number"
                            name="cancellation_policy.cancellation_time"
                            value={room.cancellation_policy.cancellation_time}
                            onChange={handleChange}
                            required
                            min="1"
                            placeholder="Minimum days before check-in"
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Cancellation Charge *</label>
                          <input
                            type="number"
                            name="cancellation_policy.cancellation_charge"
                            value={room.cancellation_policy.cancellation_charge}
                            onChange={handleChange}
                            required
                            min="0"
                            step="0.01"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Facilities */}
              <div className="form-section">
                <div className="section-header">
                  <h3>Facilities *</h3>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={addFacility}
                  >
                    + Add Facility
                  </button>
                </div>

                {room.facilities.map((facility, index) => (
                  <div key={index} className="dynamic-field-row">
                    <div className="form-grid">
                      <div className="form-group">
                        <input
                          type="text"
                          placeholder="Facility Name"
                          value={facility.name}
                          onChange={(e) => handleFacilityChange(index, 'name', e.target.value)}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <input
                          type="text"
                          placeholder="Description"
                          value={facility.desc}
                          onChange={(e) => handleFacilityChange(index, 'desc', e.target.value)}
                        />
                      </div>

                      <div className="form-group">
                        <input
                          type="number"
                          placeholder="Price"
                          value={facility.price}
                          onChange={(e) => handleFacilityChange(index, 'price', e.target.value)}
                          min="0"
                          step="0.01"
                        />
                      </div>

                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => deleteFacility(index)}
                        title="Delete"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}

                {room.facilities.length === 0 && (
                  <p className="help-text">Please add at least one facility</p>
                )}
              </div>

              {/* Features */}
              <div className="form-section">
                <div className="section-header">
                  <h3>Features *</h3>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={addFeature}
                  >
                    + Add Feature
                  </button>
                </div>

                {room.features.map((feature, index) => (
                  <div key={index} className="dynamic-field-row">
                    <div className="form-grid">
                      <div className="form-group">
                        <input
                          type="text"
                          placeholder="Feature Type"
                          value={feature.type}
                          onChange={(e) => handleFeatureChange(index, 'type', e.target.value)}
                        />
                      </div>

                      <div className="form-group">
                        <input
                          type="text"
                          placeholder="Description"
                          value={feature.desc}
                          onChange={(e) => handleFeatureChange(index, 'desc', e.target.value)}
                        />
                      </div>

                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => deleteFeature(index)}
                        title="Delete"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}

                {room.features.length === 0 && (
                  <p className="help-text">Please add at least one feature</p>
                )}
              </div>

              {/* Images */}
              <div className="form-section">
                <h3>Room Images * (Max 10)</h3>
                <div className="form-group">
                  {!loader ? (
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageSelect}
                    />
                  ) : (
                    <p>Uploading...</p>
                  )}
                </div>

                {room.image.length > 0 && (
                  <div className="image-gallery">
                    {room.image.map((img, index) => (
                      <div key={index} className="image-item">
                        <img src={img} alt={`Room ${index + 1}`} />
                        <button
                          type="button"
                          className="btn-remove-image"
                          onClick={() => handleImageRemove(img, index)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {room.image.length === 0 && (
                  <p className="help-text">Please upload at least one image</p>
                )}
              </div>

              {/* Submit Button */}
              <div className="form-actions">
                <button type="submit" className="btn btn-primary btn-lg">
                  {room._id ? 'Update Room' : 'Create Room'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-lg"
                  onClick={() => {
                    resetRoom();
                    setListView(true);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteDialog && (
          <div className="modal-overlay">
            <div className="modal">
              <h3>Confirm Delete</h3>
              <p>Are you sure you want to delete this room? This action cannot be undone.</p>
              <div className="modal-actions">
                <button
                  className="btn btn-danger"
                  onClick={handleDelete}
                >
                  Yes, Delete
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteDialog(false);
                    setDeleteRoomId(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomManagement;
